import { useState, useEffect } from 'react';
import { useForm } from 'react-hook-form';
import { Login } from '../../services/login.service';
import { useTranslation } from 'react-i18next';
import { LocalStorageService } from '../../services/local-storage.service';
import { STORAGE } from '../../constants';
import { useNavigate, Link } from 'react-router-dom';
import { AlertService } from '../../services/alert.service';
import useInputValidation from '../../hooks/use-input-validation.hook';
import Button from '../../components/forms/button.component';
import Input from '../../components/forms/input.component';
import Icon from '../../components/forms/icon.component';
import BgBody from '../../components/layout/bg-body.component';
import useBgPages from '../../hooks/use-bg-pages.hook';
import Confetti from 'react-confetti';
import { t } from 'i18next';

declare const KTComponents: any;

const ProgressItemLogin = ({ progress, index, labelKey, labelNumber = 1 }: any) => {
  const statusClass = progress[index] ? 'primary' : 'danger';
  const textClass = progress[index] ? 'primary' : 'muted';

  return (
    <div className="d-flex align-items-center">
      <div className={`bullet bullet-dot h-7px w-7px mb-0 rounded- bg-${statusClass}`} />
      <div className={`ms-2 text-${textClass} fw-bolder`}>{labelNumber}</div>
      <div className={`ms-2 text-${textClass} flex-shrink-0 fw-bolder`}>{t(labelKey)}</div>
    </div>
  );
};

const verifyPasswordForm: React.FC = () => {
  useBgPages();
  const { t } = useTranslation();
  const [stepPassword, setStepPassword] = useState('loading');
  const session = LocalStorageService.get(STORAGE.SESSION);
  const token = LocalStorageService.get(STORAGE.TOKEN);
  const navigate = useNavigate();
  const pathParams = location.pathname.split('/');
  const idUser = pathParams[2];
  const UUID = pathParams[3];
  const [progress, setProgress] = useState([false, false, false, false, false]);
  const { passwordRegex } = useInputValidation();
  const [showConfetti, setShowConfetti] = useState(false);

  const consecutiveTrueCount = progress.reduce((count, value) => {
    if (value) {
      count += 1;
    } else {
      return count;
    }
    return count;
  }, 0);

  const defaultValues = {
    password: '',
    confirmPassword: ''
  };

  const {
    register,
    handleSubmit,
    watch,
    reset,
    setValue,
    trigger,
    formState: { errors, isValid, isSubmitting }
  } = useForm({
    mode: 'onChange',
    defaultValues
  });
  /password/i;

  const passwordValue = watch('password') || '';
  const passwordPattern = new RegExp(`^${passwordValue.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')}$`);

  const validatePassword = (password: any) => {
    const lengthCriteria = password.length == 10;
    const lowercaseCriteria = /[a-z]/.test(password);
    const uppercaseCriteria = /[A-Z]/.test(password);
    const numberCriteria = /[0-9]/.test(password);
    const specialCriteria = /[!@#$%¡¿/&*()._+{}<>?]/.test(password);

    setProgress([
      specialCriteria,
      numberCriteria,
      uppercaseCriteria,
      lowercaseCriteria,
      lengthCriteria
    ]);
  };

  const handleInputChange = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const password = event.target.value;
    await validatePassword(password);
    setValue('password', password, { shouldValidate: true });
    trigger('password');
    reset({
      ...defaultValues,
      password: watch('password'),
      confirmPassword: ''
    });
  };

  const generatePassword = () => {
    const length = 10;
    const lowercase = 'abcdefghijkmnopqrstuvwxyz';
    const uppercase = 'ABCDEFGHJKLMNOPQRSTUVWXYZ';
    const numbers = '0123456789';
    const special = '!@#$%¡¿/&*()_+{}<>?';

    const allChars = lowercase + uppercase + numbers + special;
    let newPassword = '';

    newPassword += lowercase[Math.floor(Math.random() * lowercase.length)];
    newPassword += uppercase[Math.floor(Math.random() * uppercase.length)];
    newPassword += numbers[Math.floor(Math.random() * numbers.length)];
    newPassword += special[Math.floor(Math.random() * special.length)];

    for (let i = 4; i < length; i++) {
      newPassword += allChars[Math.floor(Math.random() * allChars.length)];
    }

    newPassword = newPassword
      .split('')
      .sort(() => 0.5 - Math.random())
      .join('');

    const typeText = (text: string, index = 0) => {
      if (index < text.length) {
        setValue('password', text.slice(0, index + 1), {
          shouldValidate: true
        });
        setTimeout(() => typeText(text, index + 1), 50);
      } else {
        trigger('password');
      }
    };
    typeText(newPassword);
    reset({
      ...defaultValues,
      password: watch('password'),
      confirmPassword: ''
    });
  };

  const verifyRequest = async () => {
    try {
      const response = await Login.verifyPasswordRequest({ user: idUser, uuid: UUID });
      if (response.valid) setStepPassword('accepted');
    } catch (errorCode: any) {
      console.log('Error: ', errorCode);
      setStepPassword('denied');
    }
  };

  const submit = async (data: any) => {
    try {
      const response = await Login.changePassword({
        user: idUser,
        uuid: UUID,
        password: data.confirmPassword
      });
      if (response.status) setStepPassword('password-changed');
      setShowConfetti(true);
      setTimeout(() => {
        navigate('/');
      }, 6000);
    } catch (errorCode: any) {
      console.log('Error: ', errorCode);
      setStepPassword('password-changed-error');
    }
  };

  useEffect(() => {
    if (session || token) {
      navigate('/auth/login');
    }
  }, [session, token]);

  useEffect(() => {
    const timeout = setTimeout(() => {
      verifyRequest();
    }, 2000);
    return () => clearTimeout(timeout);
  }, []);

  useEffect(() => {
    const subscription = watch((value) => {
      validatePassword(value.password);
    });
    return () => subscription.unsubscribe();
  }, [watch]);

  KTComponents.init();

  return (
    <>
      <BgBody />
      <div className="d-flex flex-column flex-lg-row flex-column-fluid bg-trans min-vh-100">
        <div className="d-flex flex-column flex-lg-row-fluid align-items-center justify-content-center py-10 bg-p-dark">
          <div className="container d-flex flex-column flex-lg-row align-items-center justify-content-between p-5 margin-container">
            {stepPassword === 'accepted' ? (
              <>
                <div className="col-lg-5 text-center text-lg-start">
                  <h1 className="text-primary fw-bold mb-3 fs-3x animated-Alert">
                    {t('verify-Password.accepted.title')}
                  </h1>
                  <p className="text-white flex-shrink-0 mb-10 inputb">
                    {t('verify-Password.accepted.question')}{' '}
                    <Link className="text-info inputb fw-bolder" to="/">
                      {t('verify-Password.accepted.login-link')}
                    </Link>
                  </p>
                  <>
                    <form onSubmit={handleSubmit(submit)}>
                      <div className="fv-row mb-10 inputb">
                        <Input
                          typeBg={'text-white'}
                          uniqueTheme
                          page="login"
                          label={t('verify-Password.accepted.input-password')}
                          type={'password'}
                          requiredIndicator={'required'}
                          placeholder={t('verify-Password.accepted.input-placeholder-password')}
                          errors={errors.password}
                          {...register('password', {
                            required: true,
                            setValueAs: (value) => value.trim(),
                            pattern: {
                              value: passwordRegex,
                              message: t('verify-Password.accepted.error-invalid')
                            }
                          })}
                          onChange={(e: any) => {
                            const noSpaces = e.target.value.replace(/\s/g, '');
                            setValue('password', noSpaces, { shouldValidate: true });
                            handleInputChange(e);
                          }}
                          onPaste={(e: any) => {
                            const pasted = e.clipboardData.getData('text');
                            if (/\s/.test(pasted)) {
                              AlertService.toastInfo(
                                '¡ Atención !, no se puede copiar tu contraseña, estas tratando de copiar una contraseña con espacios.'
                              );
                              e.preventDefault();
                            }
                          }}
                        />
                        <div className="d-flex align-items-center mt-3 mb-3">
                          {progress.map((isActive, index) => (
                            <div
                              key={index}
                              className={`bg-dark flex-grow-1 bg-secondary ${
                                index < consecutiveTrueCount ? 'bg-active-primary' : ''
                              } ${index < consecutiveTrueCount ? 'active' : ''} rounded h-5px ${
                                index < progress.length - 1 ? 'me-2' : ''
                              }`}
                            />
                          ))}
                        </div>
                        <div className="text-muted">
                          <div className="mb-5 text-muted flex-shrink-0">
                            {t('verify-Password.accepted.txt-config1')}
                          </div>
                          <ProgressItemLogin
                            progress={progress}
                            index={4}
                            labelKey="verify-Password.accepted.txt-config2"
                            labelNumber={10}
                          />
                          <ProgressItemLogin
                            progress={progress}
                            index={3}
                            labelKey="verify-Password.accepted.txt-config3"
                          />
                          <ProgressItemLogin
                            progress={progress}
                            index={2}
                            labelKey="verify-Password.accepted.txt-config4"
                          />
                          <ProgressItemLogin
                            progress={progress}
                            index={1}
                            labelKey="verify-Password.accepted.txt-config5"
                          />
                          <ProgressItemLogin
                            progress={progress}
                            index={0}
                            labelKey="verify-Password.accepted.txt-config6"
                          />

                          <div className="mb-5 text-muted flex-shrink-0">
                            {t('verify-Password.accepted.txt-config7')}{' '}
                            <button
                              type="button"
                              className="btn text-info fw-bolder cursor-pointer mb-1"
                              onClick={() => generatePassword()}
                            >
                              {t('verify-Password.accepted.txt-config8')}
                            </button>
                          </div>
                        </div>
                      </div>

                      <div className="fv-row mb-15 inputb">
                        <Input
                          typeBg={'text-white'}
                          uniqueTheme
                          label={t('verify-Password.accepted.input-ConfirmPassword')}
                          requiredIndicator={'required'}
                          page="login"
                          type={'password'}
                          placeholder={t(
                            'verify-Password.accepted.input-placeholder-ConfirmPassword'
                          )}
                          errors={errors.confirmPassword}
                          {...register('confirmPassword', {
                            required: true,
                            setValueAs: (value) => value.trim(),
                            pattern: {
                              value: passwordPattern,
                              message: t('verify-Password.accepted.error-match')
                            }
                          })}
                          onChange={(e: any) => {
                            const noSpaces = e.target.value.replace(/\s/g, '');
                            setValue('confirmPassword', noSpaces, { shouldValidate: true });
                          }}
                          onPaste={(e: any) => {
                            const pasted = e.clipboardData.getData('text');
                            if (/\s/.test(pasted)) {
                              AlertService.toastInfo(
                                '¡ Atención !, no se puede copiar tu contraseña, estas tratando de copiar una contraseña con espacios.'
                              );
                              e.preventDefault();
                            }
                          }}
                        />
                      </div>
                      <div className="text-end">
                        <Button
                          type="submit"
                          className="btn btn-lg btn-primary mb-3"
                          disabled={!isValid}
                          isLoading={isSubmitting}
                          onClick={handleSubmit(submit)}
                        >
                          {t('verify-Password.accepted.recover-btn-submit')}
                          <Icon icon="ki-key" className="ms-2 fs-1" />
                        </Button>
                      </div>
                    </form>
                  </>
                </div>
                <div className="col-lg-6 d-flex justify-content-center mb-20 animated-Alert">
                  <img
                    src="/assets/media/illustrations/undraw/recover-pass-2.svg"
                    alt="Cambia tu contraseña"
                    className="img-fluid mt-15 link-caducated"
                  />
                </div>
              </>
            ) : stepPassword === 'password-changed' ? (
              <>
                {showConfetti && <Confetti width={window.innerWidth} height={window.innerHeight} />}
                <div className="col-lg-6 text-center text-lg-start">
                  <h1 className="text-white fw-bold mb-3 fs-4x inputb">
                    {t('verify-Password.password-changed.title')}
                  </h1>
                  <h1 className="text-info fw-bold mb-3 fs-2x inputb">
                    {t('verify-Password.password-changed.subtitle')}
                  </h1>
                  <p className="text-gray-500 fs-4 mb-10 inputb">
                    {t('verify-Password.password-changed.text')}
                  </p>
                </div>
                <div className="col-lg-6 d-flex justify-content-center">
                  <img
                    src="/assets/media/illustrations/undraw/success.svg"
                    alt="Contraseña actualizada"
                    className="img-fluid mt-15 link-caducated"
                  />
                </div>
              </>
            ) : stepPassword === 'password-changed-error' ? (
              <>
                <div className="col-lg-6 text-center text-lg-start">
                  <h1 className="text-danger fw-bold mb-3 fs-4x animated-Alert">
                    {t('errors.title-404')}
                  </h1>
                  <h1 className="text-white fw-bold mb-3 fs-3x inputb">
                    {t('verify-Password.password-changed-error.title')}
                  </h1>
                  <p className="text-gray-500 fs-2 mb-10 inputb">
                    {t('verify-Password.password-changed-error.subtitle')}
                  </p>
                  <Link className="btn btn-primary btn-lg rounded inputb" to="/">
                    {t('errors.btn-404')}
                  </Link>
                </div>
                <div className="col-lg-6 d-flex justify-content-center">
                  <img
                    src="/assets/media/illustrations/undraw/error.svg"
                    alt="Error al actualizar contraseña"
                    className="img-fluid mt-15 link-caducated"
                  />
                </div>
              </>
            ) : stepPassword === 'denied' ? (
              <>
                <div className="col-lg-6 text-center text-lg-start">
                  <h1 className="text-danger fw-bold mb-3 fs-4x animated-Alert">
                    {t('errors.title-404')}
                  </h1>
                  <h1 className="text-white fw-bold mb-3 fs-3x inputb">
                    {t('verify-Password.denied.title')}
                  </h1>
                  <p className="text-gray-500 fs-2 mb-10 inputb">
                    {t('verify-Password.denied.subtitle')}
                  </p>
                  <Link className="btn btn-primary btn-lg rounded inputb" to="/">
                    {t('errors.btn-404')}
                  </Link>
                </div>
                <div className="col-lg-6 d-flex justify-content-center">
                  <img
                    src="/assets/media/illustrations/undraw/notify-2.svg"
                    alt="Link caducado o no exite la url"
                    className="img-fluid mt-15 link-caducated"
                  />
                </div>
              </>
            ) : (
              stepPassword === 'loading' && (
                <>
                  <div className="col-lg-6 text-center text-lg-start">
                    <h1 className="text-info fw-bold mb-3 fs-4x inputb">
                      {t('verify-Password.loading.title')}
                    </h1>
                    <h1 className="text-white fw-bold mb-3 fs-3x inputb">
                      {t('verify-Password.loading.subtitle')}
                    </h1>
                    <p className="text-gray-500 fs-2 mb-10 inputb">
                      {' '}
                      {t('verify-Password.loading.waiting')}
                    </p>
                  </div>
                  <div className="col-lg-6 d-flex justify-content-center">
                    <img
                      src="/assets/media/illustrations/undraw/loading-2.svg"
                      alt="Validando información"
                      className="img-fluid mt-15 link-caducated"
                    />
                  </div>
                </>
              )
            )}
          </div>
        </div>
      </div>
      <div className="position-absolute bottom-0 start-50 translate-middle-x text-center w-100 py-10 margin-footer">
        <img
          src="/assets/media/logos/logo-c.svg"
          title="logo-footer"
          height="50px"
          className="img-footer"
        />
        <span className="text-muted fs-6 fw-semibold me-2 text-nowrap text-footer">
          {new Date().getFullYear()} © ChatBridge
        </span>
      </div>
    </>
  );
};

export default verifyPasswordForm;
